"use client";
import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { todayStr } from "../utils";
import {
  PlayArrow,
  Pause,
  LocationOn,
  History as HistoryIcon,
  Room,
  Speed as SpeedIcon,
  BatteryStd,
  Schedule,
} from "@mui/icons-material";

const StaffOverlay = ({
  selectedStaff,
  showHistory,
  setShowHistory,
  selectedDate,
  setSelectedDate,
  playIndex,
  setPlayIndex,
  playing,
  setPlaying,
  historyPath = [],
  historyLoading = false,
  historyError = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const glassBg = isDark ? "rgba(22,27,34,0.72)" : "rgba(211, 211, 211, 0.3)";
  const panelBg = isDark ? "rgba(28,33,43,0.98)" : "rgba(255,255,255,0.98)";

  const getBatteryColor = (battery) => {
    const percentage = parseInt(battery);
    if (Number.isNaN(percentage)) return "default";
    if (percentage >= 70) return "success";
    if (percentage >= 30) return "warning";
    return "error";
  };

  if (!selectedStaff) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        flexDirection="column"
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
            p: 3,
            mb: 2,
          }}
        >
          <Room sx={{ fontSize: 64, color: "white" }} />
        </Box>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          No Staff Selected
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Select a team member from the list to view their location
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Top Buttons */}
      <Card
        sx={{
          position: "absolute",
          top: { xs: 40, sm: -15 },
          left: { xs: "46%", sm: "50%" },
          transform: "translateX(-50%)",
          zIndex: 1000,
          background: glassBg,
          borderRadius: "12px",
          display: "flex",
          gap: 1,
          alignItems: "center",
          backdropFilter: "blur(8px)",
        }}
      >
        <Button
          variant={showHistory ? "outlined" : "contained"}
          startIcon={<LocationOn />}
          sx={{ width: "120px" }}
          onClick={() => {
            setShowHistory(false);
            setPlaying(false);
            setPlayIndex(0);
          }}
          size="small"
        >
          Live View
        </Button>
        <Button
          variant={showHistory ? "contained" : "outlined"}
          startIcon={<HistoryIcon />}
          onClick={() => {
            setShowHistory(true);
            setPlayIndex(0);
            setPlaying(false);
          }}
          size="small"
          disabled={selectedStaff.id === "all"}
        >
          History
        </Button>

        {showHistory && (
          <TextField
            type="date"
            size="small"
            value={selectedDate || ""}
            inputProps={{ max: todayStr() }}
            onChange={(e) => {
              if (!e.target.value) return;
              setSelectedDate(e.target.value);
              setPlayIndex(0);
              setPlaying(false);
            }}
            sx={{ minWidth: { xs: 100, sm: 160 }, ml: { xs: 0.5, sm: 1 } }}
          />
        )}
      </Card>

      {/* Info Card */}
      <Card
        sx={{
          position: "absolute",
          bottom: 16,
          left: 5,
          backdropFilter: "blur(8px)",
          background: glassBg,
          borderRadius: "12px",
          p: 2,
          zIndex: 1000,
          minWidth: 260,
        }}
      >
        <Typography variant="h6">{selectedStaff.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedStaff.status}
          {showHistory && historyLoading && " · loading route…"}
          {showHistory && historyError && " · failed to load route"}
          {showHistory && !historyLoading && !historyError && historyPath.length === 0 && " · no route recorded"}
        </Typography>
        <Stack spacing={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <SpeedIcon fontSize="small" color="primary" />
            <Typography variant="body2" sx={{ flex: 1 }}>
              Speed
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {selectedStaff.speed}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <BatteryStd
              sx={{ color: `${getBatteryColor(selectedStaff.battery)}.main`.replace("default.main", "text.secondary") }}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              Battery
            </Typography>
            <Chip
              label={selectedStaff.battery}
              size="small"
              color={getBatteryColor(selectedStaff.battery)}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Schedule color="primary" />
            <Typography variant="body2" sx={{ flex: 1 }}>
              Last Update
            </Typography>
            <Typography variant="body2" fontWeight="600" color="primary.main">
              {selectedStaff.lastUpdate}
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Playback Controls */}
      {showHistory && selectedDate && (
        <Card
          sx={{
            position: "absolute",
            bottom: 16,
            left: "48%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            gap: 1,
            alignItems: "center",
            background: panelBg,
            p: 2,
            borderRadius: "12px",
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              if (playing) setPlaying(false);
              else {
                const path = historyPath;
                if (path.length === 0) return;
                if (playIndex >= path.length - 1) setPlayIndex(0);
                setPlaying(true);
              }
            }}
            color={playing ? "secondary" : "primary"}
          >
            {playing ? <Pause /> : <PlayArrow />}
          </IconButton>

          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPlayIndex((p) => Math.max(0, p - 1));
              setPlaying(false);
            }}
            disabled={playIndex === 0}
          >
            Prev
          </Button>

          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPlayIndex((p) =>
                Math.min(
                  historyPath.length - 1,
                  p + 1
                )
              );
              setPlaying(false);
            }}
            disabled={
              playIndex >=
              historyPath.length - 1
            }
          >
            Next
          </Button>

          <Box sx={{ flex: 1, minWidth: 120 }}>
            <LinearProgress
              variant="determinate"
              value={
                historyPath.length
                  ? ((playIndex + 1) /
                      historyPath.length.length) *
                    100
                  : 0
              }
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{ minWidth: 60, textAlign: "center" }}
          >
            {historyPath.length
              ? `${Math.min(
                  playIndex + 1,
                  historyPath.length.length
                )} / ${historyPath.length.length}`
              : "0 / 0"}
          </Typography>
        </Card>
      )}
    </>
  );
};

export default StaffOverlay;
